import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ClerkProvider, SignedIn, useAuth, useUser, UserButton } from '@clerk/clerk-react';
import LandingPage from './LandingPage';
import Dashboard from './Dashboard';
import InputPage from './InputPage';
import ResultsPageSPV from './ResultsPageSPV';
import InputPageIndividualBRT from './InputPageIndividualBRT';
import ResultsPageIndividualBRT from './ResultsPageIndividualBRT';
import HowItWorksPage from './HowItWorksPage';
import InviteOnlyRegistration from './InviteOnlyRegistration';
import AdminPanel from './AdminPanel';
import { SignIn } from "@clerk/clerk-react";
import { SignUp } from "@clerk/clerk-react";

const clerkPubKey = 'pk_test_bWVycnktZ2FubmV0LTgxLmNsZXJrLmFjY291bnRzLmRldiQ';

// DevTools Protection
function disableDevTools() {
  // Disable right-click
  document.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    return false;
  });

  // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
  document.addEventListener('keydown', (e) => {
    if (e.keyCode === 123 || 
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74)) || 
        (e.ctrlKey && e.keyCode === 85)) {
      e.preventDefault();
      return false;
    }
  });

  // Detect DevTools
  const threshold = 160;
  setInterval(() => {
    if (window.outerHeight - window.innerHeight > threshold || 
        window.outerWidth - window.innerWidth > threshold) {
      console.clear();
      console.log('%cSTOP!', 'color: red; font-size: 50px; font-weight: bold;');
      console.log('%cThis is a browser feature intended for developers.', 'color: red; font-size: 16px;');
      console.log('%c© BTL Calculator Company Ltd - Unauthorized access is prohibited', 'color: blue; font-size: 14px;');
    }
  }, 500);
}

// Session Manager with 20-minute inactivity timeout
const SessionManager = () => {
  const { isSignedIn, sessionId } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    if (isSignedIn && user && sessionId) {
      let inactivityTimer;

      // Function to reset the timer
      const resetTimer = () => {
        clearTimeout(inactivityTimer);
        
        // Set 20-minute timer (20 * 60 * 1000 = 1,200,000 milliseconds)
        inactivityTimer = setTimeout(() => {
          // Auto-logout after 20 minutes
          alert('You have been automatically logged out due to 20 minutes of inactivity.');
          
          // NUCLEAR OPTION - Force them out completely!
          localStorage.clear();
          sessionStorage.clear();
          document.cookie.split(";").forEach(function(c) { 
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
          });
          window.location.replace('/sign-in');
        }, 20 * 60 * 1000); // 20 minutes
      };

      // Events that count as "activity"
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

      // Add event listeners for user activity
      const activityHandler = () => resetTimer();
      
      activityEvents.forEach(event => {
        document.addEventListener(event, activityHandler, true);
      });

      // Start the timer
      resetTimer();

      // Start session tracking (your existing code)
      fetch('/api/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'start',
          email: user.primaryEmailAddress?.emailAddress,
          sessionId: sessionId
        })
      });

      // Check session every 30 seconds (your existing code)
      const interval = setInterval(async () => {
        try {
          const response = await fetch('/api/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'check',
              email: user.primaryEmailAddress?.emailAddress,
              sessionId: sessionId
            })
          });
          
          const result = await response.json();
          
          if (!result.isActive) {
            alert('Your session has expired due to another login. Click OK to close this session and investigate why someone else is using your login details.');

            // NUCLEAR OPTION - Force them out completely!
            localStorage.clear();
            sessionStorage.clear();
            document.cookie.split(";").forEach(function(c) { 
              document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            });
            window.location.replace('/sign-in');
          }
        } catch (error) {
          console.error('Session check failed:', error);
        }
      }, 30000); // Check every 30 seconds

      // Cleanup function
      return () => {
        clearInterval(interval);
        clearTimeout(inactivityTimer);
        activityEvents.forEach(event => {
          document.removeEventListener(event, activityHandler, true);
        });
      };
    }
  }, [isSignedIn, user, sessionId]);

  return null;
};

function App() {
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    disableDevTools();
  }, []);

  return (
    <ClerkProvider 
      publishableKey={clerkPubKey}
      afterSignInUrl="/calculator-suite"
      afterSignUpUrl="/calculator-suite"
      appearance={{
        baseTheme: undefined,
        variables: {
          colorPrimary: '#007bff',
          colorPrimaryText: '#ffffff',
          colorBackground: '#ffffff',
          colorInputBackground: '#ffffff',
          colorInputText: '#333333',
          colorText: '#333333',
          colorTextSecondary: '#666666',
          colorSuccess: '#28a745',
          colorDanger: '#dc3545',
          colorWarning: '#ffc107',
          fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontSize: '16px',
          borderRadius: '8px',
          spacingUnit: '1.2rem'
        },
        elements: {
          card: {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: 'none',
            borderRadius: '8px',
            backgroundColor: 'white'
          },
          logoBox: {
            display: 'none'
          },
          logoImage: {
            display: 'none'
          },
          headerTitle: {
            display: 'none'
          },
          headerSubtitle: {
            display: 'none'
          },
          formFieldInput: {
            backgroundColor: '#ffffff',
            border: '2px solid #e1e5e9',
            borderRadius: '8px',
            fontSize: '16px',
            padding: '16px',
            '&:focus': {
              borderColor: '#007bff',
              boxShadow: '0 0 0 3px rgba(0, 123, 255, 0.1)'
            }
          },
          formFieldLabel: {
            color: '#333333',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px'
          },
          formButtonPrimary: {
            backgroundColor: '#007bff',
            borderColor: '#007bff',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            padding: '16px 24px',
            boxShadow: '0 2px 4px rgba(0, 123, 255, 0.2)',
            '&:hover': {
              backgroundColor: '#0056b3',
              borderColor: '#0056b3',
              boxShadow: '0 4px 8px rgba(0, 123, 255, 0.3)'
            }
          },
          footerActionLink: {
            color: '#007bff',
            fontSize: '14px',
            '&:hover': {
              color: '#0056b3'
            }
          },
          socialButtonsBlockButton: {
            display: 'none !important'
          },
          socialButtonsBlockButtonText: {
            display: 'none !important'  
          },
          formFieldErrorText: {
            color: '#dc3545',
            fontSize: '14px'
          }
        },
        layout: {
          showOptionalFields: false
        },
      }}
    >
      <SessionManager />
      <Router>
        <div style={{ position: 'relative' }}>
          
          {/* User Button for signed-in users - only on calculator pages */}
          <SignedIn>
            <div style={{
              position: 'fixed',
              top: '10px',
              right: '10px',
              zIndex: 1000
            }}>
              <UserButton afterSignOutUrl="/" />
            </div>
          </SignedIn>
          
         {/* Routes - Available to everyone */}
<Routes>
  <Route path="/" element={<LandingPage />} />
  
  {/* How It Works - Public access for marketing */}
  <Route path="/how-it-works" element={<HowItWorksPage />} />
  
  {/* Invite-only registration */}
  <Route path="/register" element={<InviteOnlyRegistration />} />
  <Route path="/signup" element={<InviteOnlyRegistration />} />
  {/* Admin Panel - Secret door! */}
<Route path="/admin" element={<AdminPanel />} />
  
  {/* Calculator Suite - only for signed-in users */}
            <Route path="/calculator-suite" element={
              <SignedIn>
                <Dashboard />
              </SignedIn>
            } />
            
            {/* Protected routes - only for signed-in users */}
            <Route path="/spv" element={
              <SignedIn>
                <InputPage setFormData={setFormData} />
              </SignedIn>
            } />
            <Route path="/results" element={
              <SignedIn>
                <ResultsPageSPV formData={formData} />
              </SignedIn>
            } />
            <Route path="/individual" element={
              <SignedIn>
                <InputPageIndividualBRT setFormData={setFormData} />
              </SignedIn>
            } />
            <Route path="/results-individual" element={
              <SignedIn>
                <ResultsPageIndividualBRT formData={formData} />
              </SignedIn>
            } />
            <Route path="/sign-in" element={
              <div style={{
                minHeight: '100vh',
                background: '#007bff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}>
                <SignIn afterSignInUrl="/calculator-suite" />
              </div>
            } />
            <Route path="/sign-up" element={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#ffffff',
                padding: '20px'
              }}>
                <div style={{
                  background: '#ffffff',
                  borderRadius: '16px',
                  padding: '40px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #dee2e6',
                  maxWidth: '450px',
                  width: '100%'
                }}>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '30px'
                  }}>
                    <img 
                      src="/Logo 09 25.png" 
                      alt="BTL Calculator Logo" 
                      style={{
                        height: '60px',
                        width: 'auto',
                        marginBottom: '15px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <h1 style={{
                      color: '#333',
                      fontSize: '28px',
                      fontWeight: 'bold',
                      marginBottom: '10px'
                    }}>
                      Join Our Platform
                    </h1>
                    <p style={{
                      color: '#666',
                      fontSize: '16px',
                      marginBottom: '8px'
                    }}>
                      Get access to professional Buy-to-Let calculators
                    </p>
                    <p style={{
                      color: '#888',
                      fontSize: '14px',
                      margin: 0,
                      fontStyle: 'italic'
                    }}>
                      Invite-only registration • Premium tools for professionals
                    </p>
                  </div>
                  <SignUp afterSignUpUrl="/calculator-suite" />
                </div>
              </div>
            } />
          </Routes>
          
        </div>
      </Router>
    </ClerkProvider>
  );
}

export default App;